import { FC, Fragment, useCallback } from 'react';
import {
  Button,
  ButtonGroup,
  Dropdown,
  Icon,
  IconButton,
  Image,
  logos,
  Menu,
  MenuItem,
  Spacer,
  Stack,
  TableCell,
  Tag,
  TextBody,
  TextOverline,
  Tooltip,
} from '@pypestream/design-system';
import { TeamName } from './team-name';
import { RenderViaTeamDetails } from './render-via-team-details';
import './permissions-table.scss';
import {
  HandleAddTeam,
  HandleOrgCollapse,
  HandleRemoveTeam,
  HandleToggleFavoriteOrg,
  HandleUpdateManagerRole,
  ShortOrgType,
} from './permissions-table.types';
import { resolveOrgStarState } from './permissions-table-filter';
import { useTranslation } from '@pypestream/translations';

export type OrgCellProps = {
  org: ShortOrgType;
  canManageAccess: boolean;
  isAddingTeamToOrg: boolean;
  isSavingDetails: boolean;
  favoriteOrgIds: string[];
  canEditFavorites: boolean;
  handleOrgCollapse: HandleOrgCollapse;
  handleUpdateManagerRole: HandleUpdateManagerRole;
  handleRemoveTeam?: HandleRemoveTeam;
  handleAddTeam?: HandleAddTeam;
  handleToggleFavoriteOrg: HandleToggleFavoriteOrg;
};

export const OrgCell: FC<OrgCellProps> = ({
  org,
  canManageAccess,
  isAddingTeamToOrg,
  isSavingDetails,
  favoriteOrgIds,
  canEditFavorites,
  handleUpdateManagerRole,
  handleOrgCollapse,
  handleRemoveTeam,
  handleAddTeam,
  handleToggleFavoriteOrg,
}) => {
  const [t] = useTranslation();
  const starState = resolveOrgStarState(org, favoriteOrgIds, canEditFavorites);
  const renderAccountRolesDropdown = useCallback(
    ({
      canSelectMoreRole,
      roleSelectorId,
      isAddRole,
    }: {
      canSelectMoreRole: boolean;
      roleSelectorId: string;
      isAddRole: boolean;
    }) => {
      return (
        <>
          {(canSelectMoreRole && (
            <div>
              {!isAddRole && <Icon name="chevron-down"></Icon>}
              <Dropdown trigger={roleSelectorId} placement="bottom-start">
                <Menu>
                  {org.availableAccountRoles?.map((availableAccountRole) => (
                    <MenuItem
                      key={availableAccountRole.role.id}
                      onClick={() => {
                        handleUpdateManagerRole({
                          orgId: org.id || '',
                          role: availableAccountRole,
                          operation: isAddRole ? 'add' : 'update',
                        });
                      }}
                      disabled={isSavingDetails}
                    >
                      {availableAccountRole.role.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Dropdown>
            </div>
          )) ||
            null}
        </>
      );
    },
    [
      org.availableAccountRoles,
      org.id,
      isSavingDetails,
      handleUpdateManagerRole,
    ]
  );

  const hasDirectAccountRoleAssigned = org.accountRoles?.find(
    (role) => !role.teams
  );

  return (
    <TableCell
      rowspan={org.isCollapsed ? 1 : org.orgRowSpan}
      className="c-permissions-table--org-column"
    >
      <Stack alignItems="start" gutter="2xsmall" direction="column">
        <Stack
          alignItems="center"
          gutter="2xsmall"
          style={{ width: '100%', position: 'relative' }}
        >
          <IconButton
            name={org.isCollapsed ? 'chevron-down' : 'chevron-up'}
            size="xsmall"
            onClick={(e) => {
              e.stopPropagation();
              handleOrgCollapse({
                orgId: org.id || '',
                collapse: !org.isCollapsed,
              });
            }}
          ></IconButton>
          <TextBody
            variant="secondary"
            // Reserves room for the star, which is pinned absolute to this row's
            // right edge (below) — without it a long name's text runs under it.
            style={{ paddingInlineEnd: 24 }}
          >
            {org.name}
          </TextBody>
          {canEditFavorites &&
            (starState.readOnly ? (
              // Primary/home org — neutral gray (design ref PSTUDIO-3330; the
              // nav org-selector shows no star at all for the primary org, but
              // this table still needs one to mark the row — the "Home Org"
              // label below is what carries the brand purple for this row).
              // Pinned to the row's right edge regardless of name length; the
              // Stack above is `position: relative` for this to resolve against.
              <Icon
                name={starState.icon}
                size="xsmall"
                color="gray"
                style={{
                  position: 'absolute',
                  insetInlineEnd: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              ></Icon>
            ) : (
              <IconButton
                name={starState.icon}
                size="xsmall"
                id={`org-${org.id}-favorite-star`}
                pressed={starState.icon === 'star-filled'}
                label={
                  t(
                    starState.icon === 'star-filled'
                      ? 'manager/common:permissionAndAccessTable.removeFromMyOrgs'
                      : 'manager/common:permissionAndAccessTable.addToMyOrgs'
                  ) || undefined
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleFavoriteOrg({ orgId: org.id || '' });
                }}
                style={
                  starState.icon === 'star-filled'
                    ? // Favorited — brand purple, matching the nav org-selector's
                      // filled star. `IconButton` has no `color` prop; this is the
                      // same `--ps-icon-button--color` custom property
                      // `icon-button.scss` reads. #8573e7 is `color(purple, 300)`
                      // — no JS-side token export exists for it. `pressed` also
                      // makes the button's own `::after` pseudo-element visible
                      // (its `.c-icon-btn--selected` rule), painted with
                      // `--ps-icon-button--active-bg-color` (gray-100 by default)
                      // — a filled square behind the star; force it transparent.
                      // This `IconButton` has no `variant="tertiary"` (unlike the
                      // nav org-selector's), so `--ps-icon-button--opacity` below
                      // is inert — there is no `.c-icon-btn--tertiary` ghost
                      // opacity here to override, and the star is already at full
                      // strength by default; kept for parity with the other
                      // surface rather than removed. `--ps-icon-button--hover-color`
                      // IS load-bearing here — the shared base `:hover` rule reads
                      // it on every variant, and without it hover would fall back
                      // to that rule's own gray default (PSTUDIO-3330 review round 2).
                      ({
                        position: 'absolute',
                        insetInlineEnd: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        '--ps-icon-button--opacity': '1',
                        '--ps-icon-button--color': '#8573e7',
                        '--ps-icon-button--hover-color': '#8573e7',
                        '--ps-icon-button--active-bg-color': 'transparent',
                      } as React.CSSProperties)
                    : // Not favorited — hidden until the row is hovered/focused,
                      // then purple with a dashed outline (design ref
                      // PSTUDIO-3330), via the CSS variables
                      // `permissions-table.scss` sets on
                      // `.c-permissions-table--org-column`. `stroke-dasharray`
                      // is an inherited SVG property, same mechanism as
                      // `currentColor` above — `star.svg`'s `<path>` declares
                      // neither, so both cross the icon-button→icon shadow
                      // boundaries uninterrupted. `--ps-icon-button--hover-color`
                      // mirrors `--ps-icon-button--color` (not a literal hex) so
                      // the shared base `:hover` rule keeps reading the SAME
                      // reveal-driven purple instead of falling back to its own
                      // gray default on hover of the button itself (PSTUDIO-3330
                      // review round 2 — without this the star read purple on row
                      // hover but gray the instant the pointer reached it).
                      ({
                        position: 'absolute',
                        insetInlineEnd: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: 'var(--org-star-opacity, 1)',
                        '--ps-icon-button--color': 'var(--org-star-color)',
                        '--ps-icon-button--hover-color':
                          'var(--org-star-color)',
                        strokeDasharray: 'var(--org-star-dash, 0)',
                      } as React.CSSProperties)
                }
              />
            ))}
        </Stack>
        {org.isPrimaryAccount ? (
          // The logged-in user's home org (design ref PSTUDIO-3330) — takes the
          // place of the "Parent: X" line below, since it is the more salient
          // fact about this row. Indented to align with the name above it,
          // which sits past the row's own 16px chevron + 4px gutter — this
          // line has no chevron of its own, so it needs the same offset.
          <Stack
            alignItems="center"
            gutter="2xsmall"
            style={{ paddingInlineStart: 20 }}
          >
            <TextBody
              variant="tertiary"
              i18nKey="manager/common:permissionAndAccessTable.homeOrg"
            >
              Home Org
            </TextBody>
            <Icon
              name="home"
              size="xsmall"
              weight="medium"
              color="purple"
            ></Icon>
          </Stack>
        ) : (
          org.parentAccount && (
            <TextBody
              variant="tertiary"
              i18nKey="manager/common:permissionAndAccessTable.orgParent"
              i18nOptions={{ name: org.parentAccount?.name }}
              style={{ paddingInlineStart: 20 }}
            >
              {`Parent: ${org.parentAccount?.name}`}
            </TextBody>
          )
        )}
      </Stack>
      {!org.isCollapsed && (
        <>
          {(!!org.assignedTeams || !!org.availableTeams) && (
            <>
              <Spacer size="large"></Spacer>
              <Stack alignItems="center" gutter="xsmall">
                <Icon
                  name="teams"
                  size="xsmall"
                  weight="medium"
                  color="gray"
                ></Icon>
                <TextOverline>Teams</TextOverline>
                <Tooltip
                  content={
                    t(
                      'manager/common:permissionAndAccessTable.teamRoleTooltip'
                    ) || ''
                  }
                >
                  <Icon name="info" size="xsmall" color="gray" />
                </Tooltip>
              </Stack>
              <Spacer size="medium"></Spacer>
            </>
          )}
          {(org.assignedTeams && org.assignedTeams?.length > 0 && (
            <>
              <Stack direction="column">
                {org.assignedTeams?.map((team) => (
                  <Tag
                    minimal
                    removable={canManageAccess}
                    new-variant="outlined"
                    key={`${org.id}-${team.team.id}`}
                    onRemove={() => {
                      handleRemoveTeam?.({ orgId: org.id || '', team });
                    }}
                    disabled={isSavingDetails}
                  >
                    <Icon name="teams"></Icon>
                    <TeamName team={team} />
                  </Tag>
                ))}
              </Stack>
              <Spacer size="small"></Spacer>
            </>
          )) ||
            null}
          {(canManageAccess &&
            org.availableTeams &&
            org.availableTeams?.length > 0 && (
              <Fragment key={`org-${org.id}-teams-section`}>
                <Button
                  variant="ghost"
                  size="small"
                  id={`org-${org.id}-team-selector`}
                  i18nKey="manager/common:permissionAndAccessTable.addTeam"
                  disabled={isAddingTeamToOrg || isSavingDetails}
                >
                  <Icon slot="prefix" name="add"></Icon>
                  Add Team
                </Button>
                <Dropdown
                  trigger={`org-${org.id}-team-selector`}
                  placement="bottom-start"
                  closeOnClickOutside
                >
                  <Menu>
                    {org.availableTeams?.map((team) => (
                      <MenuItem
                        key={team.id}
                        onClick={() => {
                          handleAddTeam?.({ orgId: org.id || '', team });
                        }}
                      >
                        {team.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </Dropdown>
              </Fragment>
            )) ||
            (org.assignedTeams?.length == 0 && (
              <TextBody
                variant="secondary"
                size="small"
                i18nKey="manager/common:permissionAndAccessTable.teamEmptyState"
              >
                No teams found.
              </TextBody>
            )) ||
            null}
          {isAddingTeamToOrg && (
            <TextBody variant="tertiary" size="small">
              Adding team to org
            </TextBody>
          )}
          <Spacer size="xlarge"></Spacer>
          <Stack alignItems="center" gutter="xsmall">
            <Image width="16" height="16" src={logos.manager}></Image>
            <TextOverline>Manager</TextOverline>
            <Tooltip
              content={
                t(
                  'manager/common:permissionAndAccessTable.managerRoleTooltip'
                ) || ''
              }
            >
              <Icon name="info" size="xsmall" color="gray" />
            </Tooltip>
          </Stack>
          <Spacer></Spacer>
          <Stack direction="column">
            {org.accountRoles &&
              org.accountRoles?.map((role, index) => {
                const isTeamInheritedRole =
                  role.teams && role?.teams.length > 0;
                const roleSelectorId = `${org.id}-${role.role.id}-${
                  isTeamInheritedRole ? 'inherited-role' : 'direct-role'
                }`;
                const canSelectMoreRole =
                  canManageAccess &&
                  !isTeamInheritedRole &&
                  org.isPrimaryAccount &&
                  (org.availableAccountRoles || [])?.length > 0;
                return (
                  <Tag
                    minimal
                    new-variant="outlined"
                    key={roleSelectorId}
                    removable={
                      org.isPrimaryAccount &&
                      !isTeamInheritedRole &&
                      canManageAccess
                    }
                    {...(canManageAccess &&
                      canSelectMoreRole && {
                        id: roleSelectorId,
                        interactive: !!canSelectMoreRole && !isSavingDetails,
                      })}
                    onRemove={() => {
                      handleUpdateManagerRole({
                        orgId: org.id || '',
                        role,
                        operation: 'remove',
                      });
                    }}
                    disabled={isSavingDetails}
                  >
                    <Icon name={isTeamInheritedRole ? 'teams' : 'users'}></Icon>
                    {role.role.name}
                    {isTeamInheritedRole && (
                      <RenderViaTeamDetails teams={role.teams || []} />
                    )}
                    {renderAccountRolesDropdown({
                      canSelectMoreRole,
                      roleSelectorId,
                      isAddRole: false,
                    })}
                  </Tag>
                );
              })}
            {((org.accountRoles || []).length === 0 ||
              !hasDirectAccountRoleAssigned) && (
              <ButtonGroup>
                <Tag
                  minimal
                  new-variant="outlined"
                  i18nKey="manager/common:permissionAndAccessTable.roles.viewOnly"
                >
                  <Icon name="users"></Icon>
                  View Only
                </Tag>
                {org.isPrimaryAccount &&
                  !hasDirectAccountRoleAssigned &&
                  canManageAccess && (
                    <>
                      <IconButton
                        name="add"
                        id={`${org.id}-add-account-role`}
                        disabled={isSavingDetails}
                      />
                      {renderAccountRolesDropdown({
                        canSelectMoreRole:
                          org.isPrimaryAccount &&
                          (org.availableAccountRoles || [])?.length > 0,
                        roleSelectorId: `${org.id}-add-account-role`,
                        isAddRole: true,
                      })}
                    </>
                  )}
              </ButtonGroup>
            )}
          </Stack>
        </>
      )}
    </TableCell>
  );
};
